import React, { useState, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Input } from '@mellify/ui';
import { PlatformIcon } from './PlatformIcon';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { useToast } from '../../../../hooks/useToast';
import { socialAccountCallbackService } from '../../../../services/socialAccountCallbackService';
import { useQueryClient } from '@tanstack/react-query';
import { ExternalLink, AlertCircle } from 'lucide-react';

interface ConnectMastodonModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const ConnectMastodonModal: React.FC<ConnectMastodonModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [instanceUrl, setInstanceUrl] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);
  const toast = useToast();
  const queryClient = useQueryClient();

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setError(null);

      if (!currentWorkspace?.id) {
        setError('No active workspace selected');
        return;
      }

      if (!instanceUrl.trim()) {
        setError('Please enter your Mastodon instance URL');
        return;
      }

      if (!accessToken.trim()) {
        setError('Please enter your access token');
        return;
      }

      setIsSubmitting(true);

      try {
        await socialAccountCallbackService.connectMastodon({
          workspaceId: currentWorkspace.id,
          instanceUrl: instanceUrl.trim(),
          accessToken: accessToken.trim(),
        });

        toast.success(
          'Mastodon Connected',
          'Your Mastodon account has been connected successfully.',
        );

        // Invalidate social accounts query to refresh the list
        await queryClient.invalidateQueries({ queryKey: ['socialAccounts'] });

        // Reset form
        setInstanceUrl('');
        setAccessToken('');
        setError(null);

        onSuccess();
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to connect Mastodon account';
        setError(message);
      } finally {
        setIsSubmitting(false);
      }
    },
    [currentWorkspace?.id, instanceUrl, accessToken, toast, queryClient, onSuccess],
  );

  const handleClose = useCallback(() => {
    if (!isSubmitting) {
      setInstanceUrl('');
      setAccessToken('');
      setError(null);
      onClose();
    }
  }, [isSubmitting, onClose]);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      size="md"
      aria-labelledby="connect-mastodon-title"
    >
      <ModalHeader>
        <div className="flex items-center gap-3">
          <PlatformIcon platform="mastodon" size="lg" />
          <div>
            <h2
              id="connect-mastodon-title"
              className="text-lg font-semibold text-dark-900 dark:text-dark-100"
            >
              Connect Mastodon
            </h2>
            <p className="text-sm text-dark-500 dark:text-dark-400">
              Connect your Mastodon account using an access token
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Instructions */}
          <div className="rounded-lg bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 p-4">
            <h3 className="text-sm font-medium text-primary-900 dark:text-primary-200 mb-2">
              How to get your access token:
            </h3>
            <ol className="text-sm text-primary-700 dark:text-primary-300 space-y-1 list-decimal list-inside">
              <li>Go to your Mastodon instance&apos;s Settings</li>
              <li>Navigate to <strong>Development</strong> → <strong>New Application</strong></li>
              <li>Set a name (e.g., &quot;Mellify&quot;) and ensure <strong>read</strong> and <strong>write</strong> scopes are checked</li>
              <li>Click <strong>Submit</strong>, then copy the <strong>Access Token</strong></li>
            </ol>
          </div>

          {/* Instance URL */}
          <div>
            <label
              htmlFor="mastodon-instance-url"
              className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1"
            >
              Instance URL
            </label>
            <Input
              id="mastodon-instance-url"
              type="text"
              placeholder="mastodon.social"
              value={instanceUrl}
              onChange={(e) => setInstanceUrl(e.target.value)}
              disabled={isSubmitting}
              className="w-full"
            />
            <p className="mt-1 text-xs text-dark-400 dark:text-dark-500">
              Enter just the domain (e.g., mastodon.social, fosstodon.org) or the full URL
            </p>
          </div>

          {/* Access Token */}
          <div>
            <label
              htmlFor="mastodon-access-token"
              className="block text-sm font-medium text-dark-700 dark:text-dark-300 mb-1"
            >
              Access Token
            </label>
            <Input
              id="mastodon-access-token"
              type="password"
              placeholder="Paste your access token here"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              disabled={isSubmitting}
              className="w-full"
            />
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-start gap-2 rounded-lg bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 p-3">
              <AlertCircle className="w-4 h-4 text-danger-500 mt-0.5 shrink-0" />
              <p className="text-sm text-danger-700 dark:text-danger-300">{error}</p>
            </div>
          )}

          {/* Help link */}
          <div className="flex items-center gap-1 text-xs text-dark-400 dark:text-dark-500">
            <ExternalLink className="w-3 h-3" />
            <a
              href="https://docs.joinmastodon.org/client/token/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-primary-500 transition-colors"
            >
              Mastodon API documentation
            </a>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !instanceUrl.trim() || !accessToken.trim()}
            >
              {isSubmitting ? 'Connecting...' : 'Connect Account'}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

ConnectMastodonModal.displayName = 'ConnectMastodonModal';